import { LogOut, Settings, ShieldCheck } from "lucide-react";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@mailflare/ui";
import type { UserPublic } from "@mailflare/shared";

import { routes } from "@/features/mailboxes/routes";
import { useLogout } from "@/features/auth/hooks";

function initials(user: UserPublic): string {
  const source = user.displayName.length > 0 ? user.displayName : user.email;
  return source.slice(0, 2).toUpperCase();
}

export function UserMenu({ user }: { readonly user: UserPublic }): JSX.Element {
  const navigate = useNavigate();
  const logout = useLogout();
  const isAdmin = user.role === "admin" || user.role === "owner";

  const handleLogout = (): void => {
    logout.mutate(undefined, {
      onSettled: () => {
        navigate(routes.login, { replace: true });
      },
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Account menu" data-testid="user-menu">
          <Avatar className="size-7">
            <AvatarFallback>{initials(user)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <span className="block truncate">{user.displayName}</span>
          <span className="text-muted-foreground block truncate text-xs font-normal">
            {user.email}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate(routes.settings)}>
          <Settings aria-hidden />
          Settings
        </DropdownMenuItem>
        {isAdmin ? (
          <DropdownMenuItem onSelect={() => navigate(routes.admin)}>
            <ShieldCheck aria-hidden />
            Admin
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={handleLogout}>
          <LogOut aria-hidden />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
